import { Dayjs } from 'dayjs'
import { parseDate, now, today } from '../../../config/dayjsConfig'
import { FiscalYear } from '../../../types/fiscalYear/FiscalYear'
import { Contract, ContractProduct } from '../../../types/contract/Contract'
import { PeriodGroup } from '../../../types/periodGroup/PeriodGroup'
import { Filters } from '../../../redux/context/filters/types'
import { ReportDataType } from '../../../redux/context/reports/types'

export const currentAndFutureRangeSelect = (fiscalYears: FiscalYear[]): [Dayjs, Dayjs] => {
  const currentTime = now()
  const currentAndFuture = fiscalYears.filter(fiscalYear => {
    const endDate = parseDate(fiscalYear.endDate)
    return endDate && endDate.valueOf() >= currentTime.valueOf()
  })

  // If no current/future fiscal years, return today as both start and end
  if (currentAndFuture.length === 0) {
    return [currentTime, currentTime]
  }

  const firstFiscal = currentAndFuture[0]
  const lastFiscal = currentAndFuture[currentAndFuture.length - 1]

  const startDate = parseDate(firstFiscal.startDate)
  const endDate = parseDate(lastFiscal.endDate)

  return [startDate || currentTime, endDate || currentTime]
}

export const lastClosedFiscalYearsSelector = (fiscalYears: FiscalYear[]): FiscalYear | null => {
  // Filter for closed fiscal years (where isOpen is false)
  const closedFiscalYears = fiscalYears.filter(fy => !fy.isOpen)

  if (closedFiscalYears.length === 0) {
    return null
  }

  // Sort by endDate descending and return the first one (most recent)
  return closedFiscalYears.slice().sort((a, b) => {
    return new Date(b.endDate).getTime() - new Date(a.endDate).getTime()
  })[0]
}

export const currentFiscalYearSelector = (fiscalYears: FiscalYear[]) => {
  const todayDate = today()
  return fiscalYears?.find(fy => {
    const start = parseDate(fy.startDate)?.startOf('day')
    const end = parseDate(fy.endDate)?.startOf('day')
    if (!start || !end) return false
    return todayDate.isBetween(start, end, 'day', '[]')
  })
}

export const reportableFiscalYearsSelector = (contract?: Contract) => (fiscalYears: FiscalYear[]) => {
  if (!contract) return []

  // Jos nykyistä tilikautta ei löydy, käytä uusinta mennyttä/nykyistä tilikautta
  // (ei viimeistä arrayssa, joka voi olla tulevaisuudessa)
  const fromSelector = currentFiscalYearSelector(fiscalYears)
  const todayDate = today()
  const fallback = fiscalYears
    .filter(fy => {
      const endDate = parseDate(fy.endDate)?.startOf('day')
      return endDate && !endDate.isAfter(todayDate)
    })
    .sort((a, b) => {
      const dateA = parseDate(a.endDate)?.valueOf() || 0
      const dateB = parseDate(b.endDate)?.valueOf() || 0
      return dateB - dateA
    })[0]

  const current = fromSelector || fallback || fiscalYears[fiscalYears.length - 1]

  if (!current) {
    return []
  }

  let history
  let future

  switch (contract.product) {
    case ContractProduct.LIGHT:
      history = 4
      future = 1
      break
    case ContractProduct.PRO:
    case ContractProduct.BASIC:
      history = 3
      future = undefined
      break
    default:
      history = 4
      future = 1
      break
  }

  const currentIndex = fiscalYears.indexOf(current)
  const startIndex = currentIndex - history >= 0 ? currentIndex - history : 0
  const endIndex = future && currentIndex + future
  return fiscalYears.slice(startIndex, endIndex)
}

export const sortedPeriodGroupSelector = (filters: Filters, fiscalYears: FiscalYear[] = []) => {
  const { dataTypes, periodGroups } = filters
  const groups = periodGroups && periodGroups.length > 0 ? periodGroups : fiscalYears
  const sortedPeriodGroupsArray: PeriodGroup[] | FiscalYear[] = groups
    .slice()
    .sort((n1: PeriodGroup | FiscalYear, n2: PeriodGroup | FiscalYear) => +new Date(n1.endDate) - +new Date(n2.endDate))
  if (dataTypes.includes(ReportDataType.comparativePeriod)) {
    sortedPeriodGroupsArray.sort((a, b) => +new Date(b.endDate) - +new Date(a.endDate))
    sortedPeriodGroupsArray.sort((a, b) => Math.abs(a.id) - Math.abs(b.id))
  }
  return sortedPeriodGroupsArray || []
}
